import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Heart, Shuffle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { updateCommentAutoReply } from '@/services/api/comment-auto-replies';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import type { CommentAutoReply } from '@/services/api/comment-auto-replies';

interface CommentTriggerConfigProps {
    node: FlowNode;
    onClose: () => void;
    ruleData: CommentAutoReply;
}

export default function CommentTriggerConfig({ node, onClose, ruleData }: CommentTriggerConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [name, setName] = useState(ruleData.name || '');
    const [likeComment, setLikeComment] = useState(ruleData.like_comment ?? true);
    const [isRandom, setIsRandom] = useState(node.data.is_random ?? false);

    useEffect(() => {
        setName(ruleData.name || '');
        setLikeComment(ruleData.like_comment ?? true);
        setIsRandom(node.data.is_random ?? false);
    }, [ruleData, node.data.is_random]);

    const updateMutation = useMutation({
        mutationFn: async (data: { name: string; like_comment: boolean }) => {
            await updateNode(node.data.id, { is_random: isRandom });
            return updateCommentAutoReply(ruleData.id, data);
        },
        onSuccess: () => {
            toast({ title: 'Salvo!', description: 'Configurações atualizadas.' });
            queryClient.invalidateQueries({ queryKey: ['comment-auto-replies'] });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleSave = () => {
        if (!name.trim()) {
            toast({ variant: 'destructive', title: 'Nome obrigatório' });
            return;
        }
        updateMutation.mutate({ name, like_comment: likeComment });
    };

    const isSaving = updateMutation.isPending;

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="rule-name">Nome da Regra *</Label>
                        <Input
                            id="rule-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Promoção Black Friday"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsRandom(!isRandom)}
                        className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                            ${isRandom
                                ? 'border-orange-300 bg-orange-50 text-orange-600'
                                : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50 text-gray-500 cursor-pointer'
                            }`}
                        disabled={isSaving}
                    >
                        <Shuffle className="w-4 h-4 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium">Próximo node aleatório</p>
                            <p className="text-xs opacity-70">Sorteia um dos nodes conectados a este gatilho</p>
                        </div>
                        {isRandom && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0 text-orange-500" />}
                    </button>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4 text-red-400" />
                            <div>
                                <p className="text-sm font-medium text-gray-700">Curtir automaticamente</p>
                                <p className="text-xs text-gray-500">A página curte o comentário do lead</p>
                            </div>
                        </div>
                        <Switch
                            checked={likeComment}
                            onCheckedChange={setLikeComment}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                        <p className="text-xs text-cyan-800">
                            Este trigger dispara automaticamente quando alguém comenta em um post das páginas adicionadas acima.
                        </p>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
                    {isSaving ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar
                        </>
                    )}
                </Button>
            </div>
        </>
    );
}
